import {lazy} from 'react';

// Ключ в sessionStorage с временем последней перезагрузки.
// Защищает от бесконечного reload: если страница уже перезагружалась
// меньше RELOAD_COOLDOWN_MS назад, повторно не перезагружаем, а пробрасываем ошибку.
const RELOAD_TIMESTAMP_KEY = 'chunk-reload-ts';
const RELOAD_COOLDOWN_MS = 10000;

const isChunkLoadError = (error) => {
  if (!error) {
    return false;
  }
  const message = error.message || '';
  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
};

const isStaleChunkSyntaxError = (error, filename) => {
  if (!error || error.name !== 'SyntaxError') {
    return false;
  }
  const message = error.message || '';
  const isHtmlAsScript =
    /Unexpected token '<'/i.test(message) ||
    /expected expression, got '<'/i.test(message);
  const isChunkFile = typeof filename === 'string' && /\/static\/js\/.*\.js/i.test(filename);
  return isHtmlAsScript && isChunkFile;
};

const tryReloadForStaleChunk = () => {
  let lastReload = 0;
  try {
    lastReload = parseInt(window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) || '0', 10);
  } catch (e) {
    // sessionStorage недоступен (приватный режим и т.п.) — считаем, что reload ещё не было
  }

  if (Date.now() - lastReload < RELOAD_COOLDOWN_MS) {
    return false;
  }

  try {
    window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
  } catch (e) {
    // не смогли записать — всё равно перезагружаемся один раз
  }
  window.location.reload();
  return true;
};

// Обёртка над React.lazy: при ошибке загрузки устаревшего чанка
// (происходит после выката нового релиза, когда старые файлы чанков уже удалены)
// один раз перезагружает страницу, чтобы подтянуть актуальный бандл.
export const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    try {
      return await componentImport();
    } catch (error) {
      if (isChunkLoadError(error) && tryReloadForStaleChunk()) {
        // Возвращаем «вечный» промис, чтобы Suspense не отрендерил
        // фолбэк-ошибку до того, как произойдёт перезагрузка.
        return new Promise(() => {});
      }
      throw error;
    }
  });

export const installChunkErrorReload = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('error', (event) => {
    const error = event?.error;
    const filename = event?.filename || error?.filename;
    if (
      (isChunkLoadError(error) || isStaleChunkSyntaxError(error, filename)) &&
      tryReloadForStaleChunk()
    ) {
      event.preventDefault();
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const error = event?.reason;
    if (isChunkLoadError(error) && tryReloadForStaleChunk()) {
      event.preventDefault();
    }
  });
};
